import {
  DdqProdOpsQueueSortField,
  DdqProdOpsQueueSortOrder,
  DdqProdOpsTaskState,
  type DdqProdOpsWorkflowType,
} from "~/graphql/supergraphEnums";
import { formatFullDateAndTimeWithLocalTimezoneV2 } from "~/utils/datetime";
import { WORKFLOW_TYPE_LABELS } from "../labels";
import type { DdqQueueRow, DdqQueueTask } from "../types";
import type {
  GetRowIdParams,
  SortChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";

export function getDdqQueueRowId(params: GetRowIdParams<DdqQueueRow>): string {
  return params.data.id;
}

// The backend always sorts a null due date (on hold) last, in either direction; ag-grid's
// default comparator doesn't know that and would put it first on an ascending sort. ag-grid
// flips this result for descending, so the null branches counter-flip to stay last either way.
export function compareDueAt(
  valueA: string | null,
  valueB: string | null,
  _nodeA: unknown,
  _nodeB: unknown,
  isDescending: boolean,
): number {
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return isDescending ? -1 : 1;
  if (valueB == null) return isDescending ? 1 : -1;
  return new Date(valueA).getTime() - new Date(valueB).getTime();
}

// Earliest outstanding task by position, or the last one once they're all done.
export function getCurrentTask(row: DdqQueueRow): DdqQueueTask | undefined {
  const ordered = [...row.tasks].sort((a, b) => a.position - b.position);
  return (
    ordered.find((task) => task.state !== DdqProdOpsTaskState.Done) ??
    ordered[ordered.length - 1]
  );
}

export const DUE_SOON_THRESHOLD_MS = 4 * 60 * 60 * 1000;

export type DueProximity = "overdue" | "dueSoon" | null;

// The status cell tints on due-date proximity only, never on the status name — an
// `In review` or `On hold` row is not itself a warning.
export function getDueProximity(
  dueAt: string | null | undefined,
  now: number = Date.now(),
): DueProximity {
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  if (due <= now) return "overdue";
  return due - now <= DUE_SOON_THRESHOLD_MS ? "dueSoon" : null;
}

function formatDateTimeOrDash(value: string | null | undefined): string {
  if (!value) return "-";
  return formatFullDateAndTimeWithLocalTimezoneV2(value, "MMM d, yyyy, h:mm a", true);
}

// A held row has no `dueAt` — it reads as "-" rather than blank.
export function formatDueDateValue(
  params: ValueFormatterParams<DdqQueueRow, string | null>,
): string {
  return formatDateTimeOrDash(params.value);
}

export function formatCreatedAtValue(
  params: ValueFormatterParams<DdqQueueRow, string | null>,
): string {
  return formatDateTimeOrDash(params.value);
}

export function formatWorkflowTypeValue(params: ValueFormatterParams<DdqQueueRow>): string {
  if (!params.value) return "";
  const value = String(params.value);
  return value in WORKFLOW_TYPE_LABELS
    ? WORKFLOW_TYPE_LABELS[value as DdqProdOpsWorkflowType]
    : value;
}

export function getClientName(params: ValueGetterParams<DdqQueueRow>): string {
  return params.data?.arena?.name ?? "";
}

export function getOfferingNames(params: ValueGetterParams<DdqQueueRow>): string {
  return (params.data?.offerings ?? []).map((offering) => offering.name).join(", ");
}

// The only three columns the backend can sort by (see the `DdqProdOpsQueueSortField` splice).
// Every other sortable-looking column stays `sortable: false` in columnDefs.
const SORT_FIELD_BY_COLUMN_ID: Partial<Record<string, DdqProdOpsQueueSortField>> = {
  title: DdqProdOpsQueueSortField.Title,
  dueAt: DdqProdOpsQueueSortField.DueAt,
  createdAt: DdqProdOpsQueueSortField.CreatedAt,
};

export type DdqQueueSort = {
  sortBy: DdqProdOpsQueueSortField;
  sortOrder: DdqProdOpsQueueSortOrder;
};

// ag-grid reports the whole column state on every sort change; this reads off whichever one
// column (if any) is actively sorted. A cleared sort (every column's `sort` is null) reports
// `undefined` so the caller can fall back to the default rather than sending a bad pair.
export function getSortFromColumnState(
  event: SortChangedEvent<DdqQueueRow>,
): DdqQueueSort | undefined {
  const sortedColumn = event.api
    .getColumnState()
    .find((column) => column.sort != null && SORT_FIELD_BY_COLUMN_ID[column.colId ?? ""]);
  if (!sortedColumn) return undefined;

  const sortBy = SORT_FIELD_BY_COLUMN_ID[sortedColumn.colId ?? ""];
  if (!sortBy) return undefined;

  return {
    sortBy,
    sortOrder:
      sortedColumn.sort === "desc"
        ? DdqProdOpsQueueSortOrder.Desc
        : DdqProdOpsQueueSortOrder.Asc,
  };
}
